// Canvas Drawing Manager - Handles interactive canvas drawing and annotation rendering
import { drawLabels } from './canvasLabelRenderer'

export class CanvasDrawingManager {
  constructor (canvas, ctx, originalWidth, originalHeight, displayWidth, displayHeight) {
    this.canvas = canvas
    this.ctx = ctx
    this.originalWidth = originalWidth
    this.originalHeight = originalHeight
    this.displayWidth = displayWidth
    this.displayHeight = displayHeight
    this.ratio = displayWidth / originalWidth

    this.isDrawing = false
    this.mode = 'box' // 'box' | 'freehand'
    this.startPoint = null
    this.currentPoints = []
    this.previewAnnotation = null

    // Edit mode properties
    this.isEditMode = false
    this.editingAnnotation = null
    this.isDraggingAnchor = false
    this.draggedAnchorIndex = null
    this.draggedAnchorType = null // 'corner' | 'midpoint' | 'point'
    this.anchorSize = 8
    // Freehand vertices are invisible during edit (revealed on hover), so use a
    // larger hit radius than the drawn size to make them easy to grab.
    this.freehandHitRadius = 14
    // Padding (display px) added around a box for the whole-shape move hit test.
    // topPad also covers the label band drawn above the box. Shared with
    // drawHitAreas so the debug outline matches the real hit region exactly.
    this.boxHitPad = { side: 6, top: 24, bottom: 6 }

    // Move (translate whole annotation) properties
    this.isMovingAnnotation = false
    this.moveStartPoint = null
    this.moveStartAnnotation = null

    // Freehand soft-drag: when dragging a vertex, nearby vertices follow with a
    // falloff. Snapshot of the vertices at drag start (so neighbour offsets are
    // computed from a stable origin, not the already-moved live geometry).
    this.freehandDragStart = null
    // How many vertices each side follow, and the fraction of the drag delta the
    // 1st neighbour gets (decays linearly to 0 at NEIGHBOUR_SPAN + 1).
    // ponytail: tune these two knobs to taste.
    this.freehandNeighbourSpan = 3
    this.freehandNeighbourInfluence = 0.5
    // The pull also diminishes with a neighbour's spatial distance from the
    // dragged vertex: full effect when close, fading to none at this many
    // display pixels away. ponytail: calibration knob — raise for a wider pull.
    this.freehandFalloffDistance = 50
  }

  // Update dimensions when image size changes
  updateDimensions (originalWidth, originalHeight, displayWidth, displayHeight) {
    this.originalWidth = originalWidth
    this.originalHeight = originalHeight
    this.displayWidth = displayWidth
    this.displayHeight = displayHeight
    this.ratio = displayWidth / originalWidth
  }

  // Convert display coordinates to original image coordinates
  toOriginalCoords (displayX, displayY, displayW = null, displayH = null) {
    const coords = {
      x: Math.round(displayX / this.ratio),
      y: Math.round(displayY / this.ratio)
    }

    if (displayW !== null && displayH !== null) {
      coords.w = Math.round(displayW / this.ratio)
      coords.h = Math.round(displayH / this.ratio)
    }

    return coords
  }

  // Convert original coordinates to display coordinates
  toDisplayCoords (originalX, originalY, originalW = null, originalH = null) {
    const coords = {
      x: originalX * this.ratio,
      y: originalY * this.ratio
    }

    if (originalW !== null && originalH !== null) {
      coords.w = originalW * this.ratio
      coords.h = originalH * this.ratio
    }

    return coords
  }

  // Start drawing
  startDrawing (point, mode) {
    this.isDrawing = true
    this.mode = mode
    this.startPoint = point

    if (mode === 'freehand') {
      this.currentPoints = [point]
    }
  }

  // Continue drawing (mousemove)
  continueDrawing (point) {
    if (!this.isDrawing) return

    if (this.mode === 'box') {
      this.drawBoxPreview(this.startPoint, point)
    } else if (this.mode === 'freehand') {
      this.currentPoints.push(point)
      this.drawFreehandPreview()
    }
  }

  // Finish drawing (mouseup)
  finishDrawing (point) {
    if (!this.isDrawing) return null

    let annotation = null
    const minSize = 10 // Minimum size in pixels

    if (this.mode === 'box') {
      const width = Math.abs(point.x - this.startPoint.x)
      const height = Math.abs(point.y - this.startPoint.y)

      // Check minimum size
      if (width < minSize || height < minSize) {
        this.isDrawing = false
        this.clearPreview()
        return null
      }

      const displayCoords = {
        x: Math.min(this.startPoint.x, point.x),
        y: Math.min(this.startPoint.y, point.y),
        w: width,
        h: height
      }

      const originalCoords = this.toOriginalCoords(
        displayCoords.x,
        displayCoords.y,
        displayCoords.w,
        displayCoords.h
      )

      annotation = {
        id: crypto.randomUUID(),
        type: 'box',
        coords: originalCoords,
        pathology: null,
        color: '#ed1a4f'
      }
    } else if (this.mode === 'freehand') {
      if (this.currentPoints.length < 3) {
        this.isDrawing = false
        this.clearPreview()
        return null
      }

      // Simplify points (basic Douglas-Peucker)
      const simplifiedPoints = this.simplifyPoints(this.currentPoints, 2.0)

      // Convert to original coordinates
      const originalPoints = simplifiedPoints.map(p =>
        this.toOriginalCoords(p.x, p.y)
      )

      annotation = {
        id: crypto.randomUUID(),
        type: 'freehand',
        freehandpoints: originalPoints,
        pathology: null,
        color: '#ed1a4f'
      }
    }

    this.isDrawing = false
    this.startPoint = null
    this.currentPoints = []
    this.clearPreview()

    return annotation
  }

  // Draw box preview
  drawBoxPreview (start, end) {
    this.clearCanvas()
    this.ctx.strokeStyle = '#ed1a4f'
    this.ctx.lineWidth = 2
    this.ctx.setLineDash([5, 5])
    this.ctx.strokeRect(
      start.x,
      start.y,
      end.x - start.x,
      end.y - start.y
    )
    this.ctx.setLineDash([])
  }

  // Draw freehand preview
  drawFreehandPreview () {
    this.clearCanvas()
    this.ctx.strokeStyle = '#ed1a4f'
    this.ctx.lineWidth = 2
    this.ctx.beginPath()
    this.ctx.moveTo(this.currentPoints[0].x, this.currentPoints[0].y)
    for (let i = 1; i < this.currentPoints.length; i++) {
      this.ctx.lineTo(this.currentPoints[i].x, this.currentPoints[i].y)
    }
    this.ctx.stroke()
  }

  // Render all annotations
  // If editingAnnotationId is provided, only render that annotation. Markers are
  // drawn first, then labels are batched through the shared label renderer so
  // off-ROI placement, leader lines, and collision avoidance apply uniformly.
  renderAnnotations (annotations, editingAnnotationId = null, showLabels = true) {
    this.clearCanvas()

    const labels = []
    const markers = []
    annotations.forEach(annotation => {
      // If we're editing a specific annotation, only render that one
      if (editingAnnotationId && annotation.id !== editingAnnotationId) {
        return
      }

      const isEditing = editingAnnotationId != null &&
        annotation.id === editingAnnotationId
      const res = annotation.type === 'box'
        ? this.renderBox(annotation)
        : annotation.type === 'freehand'
          ? this.renderFreehand(annotation, isEditing)
          : null
      if (!res) return
      markers.push(res.marker)
      if (res.label) {
        // Attach the finding's marker so the label renderer can draw the leader
        // to the marker's nearest edge (not a fixed top anchor).
        res.label.marker = res.marker
        labels.push(res.label)
      }
    })

    if (showLabels) {
      drawLabels(this.ctx, labels, this.displayWidth, this.displayHeight, markers)
    }
  }

  // Render bounding box marker. Returns { marker, label }: marker is the
  // display-space rect (used for label collision avoidance); label is a spec
  // for the shared label renderer, or null if there's no label.
  renderBox (annotation) {
    const displayCoords = this.toDisplayCoords(
      annotation.coords.x,
      annotation.coords.y,
      annotation.coords.w,
      annotation.coords.h
    )

    this.ctx.strokeStyle = annotation.color || '#ed1a4f'
    this.ctx.lineWidth = 2
    this.ctx.strokeRect(
      displayCoords.x,
      displayCoords.y,
      displayCoords.w,
      displayCoords.h
    )

    const marker = {
      x: displayCoords.x,
      y: displayCoords.y,
      w: displayCoords.w,
      h: displayCoords.h
    }

    if (!annotation.pathology) return { marker, label: null }

    return {
      marker,
      label: {
        text: annotation.pathology,
        colour: annotation.color || '#ed1a4f',
        anchorX: displayCoords.x + displayCoords.w / 2,
        anchorY: displayCoords.y
      }
    }
  }

  // Render freehand polygon marker. Returns { marker, label }: marker is the
  // display-space bounding rect (for label collision avoidance); label is a
  // spec for the shared label renderer, or null. Returns null if no points.
  renderFreehand (annotation, isEditing = false) {
    if (!annotation.freehandpoints || annotation.freehandpoints.length === 0) return null

    const displayPoints = annotation.freehandpoints.map(p =>
      this.toDisplayCoords(p.x, p.y)
    )

    // Fill polygon (half opacity while editing so the image beneath is clearer).
    const fillAlpha = isEditing ? 0.15 : 0.3
    this.ctx.fillStyle = this.hexToRgba(annotation.color || '#ed1a4f', fillAlpha)
    this.ctx.beginPath()
    this.ctx.moveTo(displayPoints[0].x, displayPoints[0].y)
    for (let i = 1; i < displayPoints.length; i++) {
      this.ctx.lineTo(displayPoints[i].x, displayPoints[i].y)
    }
    this.ctx.closePath()
    this.ctx.fill()

    // Stroke outline
    this.ctx.strokeStyle = annotation.color || '#ed1a4f'
    this.ctx.lineWidth = 2
    this.ctx.stroke()

    const xs = displayPoints.map(p => p.x)
    const ys = displayPoints.map(p => p.y)
    const minX = Math.min(...xs)
    const minY = Math.min(...ys)
    const marker = {
      x: minX,
      y: minY,
      w: Math.max(...xs) - minX,
      h: Math.max(...ys) - minY
    }

    if (!annotation.pathology) return { marker, label: null }

    const cx = displayPoints.reduce((s, p) => s + p.x, 0) / displayPoints.length

    return {
      marker,
      label: {
        text: annotation.pathology,
        colour: annotation.color || '#ed1a4f',
        anchorX: cx,
        anchorY: minY
      }
    }
  }

  // Clear canvas
  clearCanvas () {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
  }

  // Clear preview
  clearPreview () {
    this.clearCanvas()
  }

  // Simplify points using Douglas-Peucker algorithm
  simplifyPoints (points, tolerance) {
    if (points.length <= 2) return points

    // Find the point with maximum distance
    let maxDistance = 0
    let index = 0

    for (let i = 1; i < points.length - 1; i++) {
      const distance = this.perpendicularDistance(
        points[i],
        points[0],
        points[points.length - 1]
      )
      if (distance > maxDistance) {
        maxDistance = distance
        index = i
      }
    }

    // If max distance is greater than tolerance, recursively simplify
    if (maxDistance > tolerance) {
      const left = this.simplifyPoints(points.slice(0, index + 1), tolerance)
      const right = this.simplifyPoints(points.slice(index), tolerance)

      return left.slice(0, -1).concat(right)
    } else {
      return [points[0], points[points.length - 1]]
    }
  }

  // Calculate perpendicular distance from point to line
  perpendicularDistance (point, lineStart, lineEnd) {
    const dx = lineEnd.x - lineStart.x
    const dy = lineEnd.y - lineStart.y

    const numerator = Math.abs(
      dy * point.x - dx * point.y + lineEnd.x * lineStart.y - lineEnd.y * lineStart.x
    )
    const denominator = Math.sqrt(dx * dx + dy * dy)

    return numerator / denominator
  }

  // Convert hex color to rgba
  hexToRgba (hex, alpha) {
    const r = parseInt(hex.slice(1, 3), 16)
    const g = parseInt(hex.slice(3, 5), 16)
    const b = parseInt(hex.slice(5, 7), 16)
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }

  // ===== EDIT MODE METHODS =====

  // Enable edit mode for an annotation
  setEditMode (annotation) {
    this.isEditMode = true
    this.editingAnnotation = annotation
  }

  // Disable edit mode
  clearEditMode () {
    this.isEditMode = false
    this.editingAnnotation = null
    this.isDraggingAnchor = false
    this.draggedAnchorIndex = null
    this.isMovingAnnotation = false
    this.moveStartPoint = null
    this.moveStartAnnotation = null
  }

  // Get anchors for bounding box (8 anchors: 4 corners + 4 midpoints)
  getBoxAnchors (annotation) {
    const displayCoords = this.toDisplayCoords(
      annotation.coords.x,
      annotation.coords.y,
      annotation.coords.w,
      annotation.coords.h
    )

    const { x, y, w, h } = displayCoords

    return {
      // Corners (for resizing)
      topLeft: { x, y, type: 'corner', cursor: 'nw-resize' },
      topRight: { x: x + w, y, type: 'corner', cursor: 'ne-resize' },
      bottomLeft: { x, y: y + h, type: 'corner', cursor: 'sw-resize' },
      bottomRight: { x: x + w, y: y + h, type: 'corner', cursor: 'se-resize' },
      // Midpoints (for resizing)
      topMid: { x: x + w / 2, y, type: 'midpoint', cursor: 'n-resize' },
      bottomMid: { x: x + w / 2, y: y + h, type: 'midpoint', cursor: 's-resize' },
      leftMid: { x, y: y + h / 2, type: 'midpoint', cursor: 'w-resize' },
      rightMid: { x: x + w, y: y + h / 2, type: 'midpoint', cursor: 'e-resize' }
    }
  }

  // Get anchors for freehand path (one anchor per point)
  getFreehandAnchors (annotation) {
    return annotation.freehandpoints.map((point, index) => {
      const display = this.toDisplayCoords(point.x, point.y)
      return {
        x: display.x,
        y: display.y,
        index,
        type: 'point',
        cursor: 'grab'
      }
    })
  }

  // Check if mouse is over an anchor
  getAnchorAtPoint (point, annotation) {
    if (annotation.type === 'box') {
      const threshold = this.anchorSize + 2
      const anchors = this.getBoxAnchors(annotation)
      for (const [key, anchor] of Object.entries(anchors)) {
        const dist = Math.sqrt(
          Math.pow(point.x - anchor.x, 2) + Math.pow(point.y - anchor.y, 2)
        )
        if (dist <= threshold) {
          return { key, ...anchor }
        }
      }
    } else if (annotation.type === 'freehand') {
      // Wider radius because freehand vertices are invisible until hovered.
      // Return the NEAREST vertex within range, not the first: when adjacent
      // vertices' hit circles overlap, first-match would make the lower-index
      // one swallow the overlap and leave its neighbour ungrabbable.
      const threshold = this.freehandHitRadius
      const anchors = this.getFreehandAnchors(annotation)
      let best = null
      let bestDist = Infinity
      for (const anchor of anchors) {
        const dist = Math.hypot(point.x - anchor.x, point.y - anchor.y)
        if (dist <= threshold && dist < bestDist) {
          bestDist = dist
          best = anchor
        }
      }
      return best
    }

    return null
  }

  // Draw anchor points. For boxes the standard 8 handles are always shown. For
  // freehand, vertices are hidden by default and only the `highlightIndex`
  // vertex is drawn (revealed on hover / while dragged).
  drawAnchors (annotation, highlightIndex = null) {
    if (annotation.type === 'box') {
      this.ctx.fillStyle = '#f0b93b'
      this.ctx.strokeStyle = '#15385b'
      this.ctx.lineWidth = 2
      const anchors = this.getBoxAnchors(annotation)
      for (const anchor of Object.values(anchors)) {
        this.ctx.fillRect(
          anchor.x - this.anchorSize / 2,
          anchor.y - this.anchorSize / 2,
          this.anchorSize,
          this.anchorSize
        )
        this.ctx.strokeRect(
          anchor.x - this.anchorSize / 2,
          anchor.y - this.anchorSize / 2,
          this.anchorSize,
          this.anchorSize
        )
      }
    } else if (annotation.type === 'freehand') {
      if (highlightIndex === null || highlightIndex === undefined) return
      const anchors = this.getFreehandAnchors(annotation)
      const anchor = anchors[highlightIndex]
      if (!anchor) return
      // White halo for contrast against the image, gold fill.
      this.ctx.lineWidth = 2
      this.ctx.beginPath()
      this.ctx.arc(anchor.x, anchor.y, 8, 0, Math.PI * 2)
      this.ctx.fillStyle = '#ffffff'
      this.ctx.fill()
      this.ctx.beginPath()
      this.ctx.arc(anchor.x, anchor.y, 5, 0, Math.PI * 2)
      this.ctx.fillStyle = '#f0b93b'
      this.ctx.strokeStyle = '#15385b'
      this.ctx.fill()
      this.ctx.stroke()
    }
  }

  // Distance from point p to segment a-b (clamped to the segment endpoints).
  distanceToSegment (p, a, b) {
    const dx = b.x - a.x
    const dy = b.y - a.y
    const len2 = dx * dx + dy * dy
    if (len2 === 0) {
      return Math.sqrt((p.x - a.x) ** 2 + (p.y - a.y) ** 2)
    }
    let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2
    t = Math.max(0, Math.min(1, t))
    const cx = a.x + t * dx
    const cy = a.y + t * dy
    return Math.sqrt((p.x - cx) ** 2 + (p.y - cy) ** 2)
  }

  // For a freehand annotation, find where to insert a new vertex at the given
  // display point (the nearest polygon edge, if within `threshold`). Returns
  // { insertIndex, originalPoint } or null. insertIndex is the array position
  // for splice (segmentStartIndex + 1).
  getFreehandInsertAt (annotation, displayPoint, threshold = 8) {
    if (!annotation.freehandpoints || annotation.freehandpoints.length < 2) return null
    const pts = annotation.freehandpoints.map(p => this.toDisplayCoords(p.x, p.y))
    let bestI = -1
    let bestD = Infinity
    for (let i = 0; i < pts.length; i++) {
      const a = pts[i]
      const b = pts[(i + 1) % pts.length]
      const d = this.distanceToSegment(displayPoint, a, b)
      if (d < bestD) {
        bestD = d
        bestI = i
      }
    }
    if (bestI === -1 || bestD > threshold) return null
    const original = this.toOriginalCoords(displayPoint.x, displayPoint.y)
    return { insertIndex: bestI + 1, originalPoint: original }
  }

  // Update annotation during anchor drag
  updateAnnotationFromAnchor (annotation, anchorKey, newPoint) {
    if (annotation.type === 'box') {
      return this.updateBoxFromAnchor(annotation, anchorKey, newPoint)
    } else if (annotation.type === 'freehand') {
      return this.updateFreehandFromAnchor(annotation, anchorKey, newPoint)
    }
    return annotation
  }

  // Update bounding box from anchor drag
  updateBoxFromAnchor (annotation, anchorKey, newPoint) {
    const displayCoords = this.toDisplayCoords(
      annotation.coords.x,
      annotation.coords.y,
      annotation.coords.w,
      annotation.coords.h
    )

    let { x, y, w, h } = displayCoords

    // Update based on which anchor is being dragged
    switch (anchorKey) {
      case 'topLeft':
        w += (x - newPoint.x)
        h += (y - newPoint.y)
        x = newPoint.x
        y = newPoint.y
        break
      case 'topRight':
        w = newPoint.x - x
        h += (y - newPoint.y)
        y = newPoint.y
        break
      case 'bottomLeft':
        w += (x - newPoint.x)
        h = newPoint.y - y
        x = newPoint.x
        break
      case 'bottomRight':
        w = newPoint.x - x
        h = newPoint.y - y
        break
      case 'topMid':
        h += (y - newPoint.y)
        y = newPoint.y
        break
      case 'bottomMid':
        h = newPoint.y - y
        break
      case 'leftMid':
        w += (x - newPoint.x)
        x = newPoint.x
        break
      case 'rightMid':
        w = newPoint.x - x
        break
    }

    // Ensure minimum size
    if (w < 10) w = 10
    if (h < 10) h = 10

    // Convert back to original coordinates
    const originalCoords = this.toOriginalCoords(x, y, w, h)

    return {
      ...annotation,
      coords: originalCoords
    }
  }

  // Snapshot freehand vertices at the start of a vertex drag, so soft-drag
  // neighbour offsets are measured from a stable origin.
  startFreehandVertexDrag (annotation) {
    this.freehandDragStart = (annotation.freehandpoints || []).map(p => ({ ...p }))
  }

  clearFreehandVertexDrag () {
    this.freehandDragStart = null
  }

  // Update freehand path from anchor drag. The dragged vertex snaps to the
  // pointer; the nearest `freehandNeighbourSpan` vertices on each side follow
  // by a proportional fraction of the same delta (linear falloff), giving a
  // soft, smooth reshape rather than a single sharp spike.
  updateFreehandFromAnchor (annotation, anchorIndex, newPoint) {
    const target = this.toOriginalCoords(newPoint.x, newPoint.y)
    const start = this.freehandDragStart

    // No snapshot (e.g. drag started without startFreehandVertexDrag): fall back
    // to moving just the dragged vertex.
    if (!start || !start[anchorIndex]) {
      const updatedPoints = [...annotation.freehandpoints]
      updatedPoints[anchorIndex] = target
      return { ...annotation, freehandpoints: updatedPoints }
    }

    const n = start.length
    const dragged = start[anchorIndex]
    const dx = target.x - dragged.x
    const dy = target.y - dragged.y

    const span = this.freehandNeighbourSpan
    const influence = this.freehandNeighbourInfluence

    const updated = start.map(p => ({ ...p }))
    updated[anchorIndex] = target

    // Track touched vertices so that, on a small polygon where wrap-around makes
    // a vertex reachable from both sides, only its nearest offset (largest
    // weight) applies.
    const touched = new Set([anchorIndex])
    for (let off = 1; off <= span; off++) {
      // Index falloff: 1st neighbour gets `influence`, decaying to 0 at span+1.
      const indexWeight = influence * (1 - off / (span + 1))
      for (const raw of [anchorIndex - off, anchorIndex + off]) {
        // Polygon is closed, so wrap indices around the ends.
        const wi = ((raw % n) + n) % n
        if (touched.has(wi)) continue
        touched.add(wi)

        // Distance falloff: fade the pull as the neighbour sits further from the
        // dragged vertex (measured in display px so it's resolution-independent).
        const displayDist = Math.hypot(
          start[wi].x - dragged.x,
          start[wi].y - dragged.y
        ) * this.ratio
        const proximity = Math.max(0, 1 - displayDist / this.freehandFalloffDistance)

        const weight = indexWeight * proximity
        updated[wi] = {
          x: Math.round(start[wi].x + dx * weight),
          y: Math.round(start[wi].y + dy * weight)
        }
      }
    }

    return { ...annotation, freehandpoints: updated }
  }

  // ===== MOVE (TRANSLATE WHOLE ANNOTATION) METHODS =====

  // Hit test: is the point inside the annotation (or on its border / label)?
  // Used to initiate a move of the whole shape. Anchor handles take priority
  // and are tested separately before this is called.
  isPointInAnnotation (point, annotation) {
    if (annotation.type === 'box') {
      if (!annotation.coords) return false
      const d = this.toDisplayCoords(
        annotation.coords.x,
        annotation.coords.y,
        annotation.coords.w,
        annotation.coords.h
      )
      const { side: sidePad, top: topPad, bottom: bottomPad } = this.boxHitPad
      return (
        point.x >= d.x - sidePad &&
        point.x <= d.x + d.w + sidePad &&
        point.y >= d.y - topPad &&
        point.y <= d.y + d.h + bottomPad
      )
    } else if (annotation.type === 'freehand') {
      if (!annotation.freehandpoints || annotation.freehandpoints.length < 3) return false
      const displayPoints = annotation.freehandpoints.map(p =>
        this.toDisplayCoords(p.x, p.y)
      )
      return (
        this.isPointInPolygon(point, displayPoints) ||
        this.isPointNearPolygonEdge(point, displayPoints, 6)
      )
    }
    return false
  }

  // Ray-casting point-in-polygon test.
  isPointInPolygon (point, points) {
    let inside = false
    for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
      const xi = points[i].x
      const yi = points[i].y
      const xj = points[j].x
      const yj = points[j].y
      const intersect =
        (yi > point.y) !== (yj > point.y) &&
        point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi
      if (intersect) inside = !inside
    }
    return inside
  }

  // Distance-from-edge test for grabbing a polygon border.
  isPointNearPolygonEdge (point, points, threshold) {
    for (let i = 0; i < points.length; i++) {
      const a = points[i]
      const b = points[(i + 1) % points.length]
      if (this.perpendicularDistance(point, a, b) <= threshold) return true
    }
    return false
  }

  // Begin a move operation, snapshotting the geometry at the start so the
  // translation is computed from a stable origin (no accumulation error).
  startMove (point, annotation) {
    this.isMovingAnnotation = true
    this.moveStartPoint = { x: point.x, y: point.y }
    if (annotation.type === 'box') {
      this.moveStartAnnotation = {
        ...annotation,
        coords: { ...annotation.coords }
      }
    } else {
      this.moveStartAnnotation = {
        ...annotation,
        freehandpoints: annotation.freehandpoints
          ? annotation.freehandpoints.map(p => ({ ...p }))
          : []
      }
    }
  }

  // Return the translated annotation for the current pointer position.
  moveAnnotationTo (point) {
    const start = this.moveStartPoint
    const base = this.moveStartAnnotation
    if (!start || !base) return base

    // Delta in display pixels, converted to original image coordinates.
    const dxOrig = (point.x - start.x) / this.ratio
    const dyOrig = (point.y - start.y) / this.ratio

    if (base.type === 'box') {
      return {
        ...base,
        coords: {
          x: Math.round(base.coords.x + dxOrig),
          y: Math.round(base.coords.y + dyOrig),
          w: base.coords.w,
          h: base.coords.h
        }
      }
    }
    return {
      ...base,
      freehandpoints: base.freehandpoints.map(p => ({
        x: Math.round(p.x + dxOrig),
        y: Math.round(p.y + dyOrig)
      }))
    }
  }

  // End a move operation.
  endMove () {
    this.isMovingAnnotation = false
    this.moveStartPoint = null
    this.moveStartAnnotation = null
  }
}
